import { Button } from '@app/components/various/Button';
import { EmptyState, EmptyStateButtonSeparator } from '@app/components/various/EmptyState';
import { getStockTypeLabels } from '@app/ducks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { ProductFilters } from '@typings';
import { omit } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  filters: ProductFilters;
  onFilterChange: (newFilters: ProductFilters, preserveRest?: boolean) => void;
}

export const NoResults = ({ filters, onFilterChange }: Props) => {
  const { t } = useTranslation(['common', 'products']);
  const hasActiveFilters = !isEmpty(Object.keys(omit(['search', 'stockType'], filters)));
  const stockLabels = useSelector(getStockTypeLabels);
  const isStockTypeSeparationEnabled = isDefined(filters.stockType);
  const isSearchActive = isDefined(filters.search) && !isEmpty(filters.search);
  const currentStockTypeLabel = stockLabels[filters.stockType ?? 'stock'];

  const handleClearFilters = () => {
    onFilterChange({ search: filters.search ?? '', ...(isStockTypeSeparationEnabled && { stockType: filters.stockType }) }, false);
  };

  const handleClearSearch = () => {
    onFilterChange({ search: '', ...(isStockTypeSeparationEnabled && { stockType: filters.stockType }) });
  };

  const clearSearchAction = {
    label: t('common:clear_search'),
    onClick: handleClearSearch,
  };

  const clearFiltersAction = {
    label: t('common:clear_filters'),
    onClick: handleClearFilters,
  };

  const getErrorTemplate = () => {
    if (hasActiveFilters && isSearchActive) {
      return {
        actions: [clearFiltersAction, clearSearchAction],
        ...t('products:no_results.search_and_filters', { returnObjects: true, searchQuery: filters.search }),
      };
    }

    if (isSearchActive) {
      return {
        actions: [clearSearchAction],
        ...t('products:no_results.search', { returnObjects: true, searchQuery: filters.search }),
      };
    }

    if (hasActiveFilters) {
      return {
        actions: [clearFiltersAction],
        ...t('products:no_results.filters', { returnObjects: true }),
      };
    }

    if (isStockTypeSeparationEnabled) {
      return {
        actions: [],
        ...t('products:no_results.stock_type', { returnObjects: true, stockTypeLabel: currentStockTypeLabel }),
      };
    }

    return {
      actions: [],
      ...t('products:no_results.default', { returnObjects: true }),
    };
  };

  const { title, subtitle, actions } = getErrorTemplate();

  return (
    <EmptyState title={title} subtitle={subtitle}>
      {actions.map(({ label, onClick }, index) => (
        <React.Fragment key={index}>
          {index !== 0 && <EmptyStateButtonSeparator>{t('common:or')}</EmptyStateButtonSeparator>}
          <Button color="secondary" variant="bordered" onClick={onClick}>
            {label}
          </Button>
        </React.Fragment>
      ))}
    </EmptyState>
  );
};
